import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/** Matches header desktop breakpoint — drawer scroll-lock is mobile/tablet only */
const desktopDrawerMediaQuery = matchMedia('(min-width: 1200px)');

/**
 * @returns {boolean}
 */
function isDesktopDrawerViewport() {
  return desktopDrawerMediaQuery.matches;
}

/**
 * Syncs page scroll lock with drawer state.
 * Scroll lock is applied via the `scroll-lock` attribute on `<details>` and a global
 * toggle listener in dialog.js that sets `html[scroll-lock]` (overflow: hidden).
 * The `open` attribute is only cleared after the close animation, so scroll stays
 * locked until the drawer fully closes unless we override that on desktop.
 *
 * @param {HTMLDetailsElement} details
 */
function syncDrawerScrollLock(details) {
  if (!details.hasAttribute('scroll-lock')) return;

  if (isDesktopDrawerViewport()) {
    document.documentElement.removeAttribute('scroll-lock');
    return;
  }

  const isOpen = details.classList.contains('menu-open') || details.hasAttribute('open');

  if (isOpen) {
    document.documentElement.setAttribute('scroll-lock', '');
  }
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {Comment | null} */
  #backdropPlaceholder = null;

  /** @type {Comment | null} */
  #drawerPlaceholder = null;

  /** @type {number | null} */
  #backdropAnimationFrame = null;

  /** @type {number | null} */
  #drawerAnimationFrame = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#cancelMountAnimations();
    this.#restoreFromBody(this.refs.details);
  }

  /**
   * Drawer panel and backdrop are moved to document.body, so they are no longer
   * inside <header-drawer>. `closest("header-drawer")` cannot find this instance.
   * `#header-component header-drawer` is also wrong here: it always resolves the
   * first drawer in the header (the mobile one, hidden on desktop).
   * Handle `header-drawer/…` on the instance that opened this panel.
   * @param {Event} event
   */
  #onTeleportedClick = (event) => {
    const target = event.composedPath?.()[0] ?? event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[on\\:click]');
    if (!trigger) return;

    const value = trigger.getAttribute('on:click') ?? '';

    // ID selectors belong to the global listener.
    if (value.startsWith('#')) return;

    // Still inside <header-drawer> — the global listener handles it.
    if (trigger.closest('header-drawer')) return;

    let method = value.split('/')[1];
    if (!method) return;

    method = method.replace(/\?.*/, '');
    const callback = /** @type {any} */ (this)[method];
    if (typeof callback !== 'function') return;

    callback.call(this, event);
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const path = event?.composedPath?.() ?? [];
    const clickEl = path.find((n) => n instanceof Element);
    if (clickEl?.closest('a.menu-drawer__summary-primary-link')) return;
    const isAllCategoriesTrigger = !!clickEl?.closest('.header-menu__all-categories');

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (isAllCategoriesTrigger && !this.isOpen) {
      details.setAttribute('open', '');
    }
    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      syncDrawerScrollLock(details);
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (details === this.refs.details) {
        this.#mountToBody(details);
        syncDrawerScrollLock(details);
      }

      setTimeout(() => {
        const focusContainer = this.#getDrawer(details) ?? details;
        trapFocus(focusContainer);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      this.#cancelMountAnimations();
      this.#getBackdrop(details)?.classList.remove('menu-drawer__backdrop--visible');
      this.#getDrawer(details)?.classList.remove('menu-drawer--visible');

      if (isDesktopDrawerViewport()) {
        syncDrawerScrollLock(details);
      }
    }

    const animationTarget =
      isDesktopDrawerViewport() && this.#getDrawer(details)?.dataset.teleported === 'true'
        ? this.#getDrawer(details)
        : details;

    onAnimationEnd(animationTarget ?? details, () => {
      if (details === this.refs.details) {
        this.#restoreFromBody(details);
      }

      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          const focusContainer = this.#getDrawer(this.refs.details) ?? this.refs.details;
          trapFocus(focusContainer);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * @param {HTMLDetailsElement} details
   * @returns {HTMLElement | null}
   */
  #getDrawer(details) {
    if (details === this.refs.details && this.#drawerPlaceholder?.parentNode) {
      return document.body.querySelector('.menu-drawer[data-teleported="true"]');
    }

    const drawer = details.querySelector('.menu-drawer');
    return drawer instanceof HTMLElement ? drawer : null;
  }

  /**
   * @param {HTMLDetailsElement} details
   * @returns {HTMLElement | null}
   */
  #getBackdrop(details) {
    if (details === this.refs.details && this.#backdropPlaceholder?.parentNode) {
      return document.body.querySelector('.menu-drawer__backdrop[data-teleported="true"]');
    }

    const backdrop = details.querySelector('.menu-drawer__backdrop');
    return backdrop instanceof HTMLElement ? backdrop : null;
  }

  /**
   * Move the backdrop (and drawer panel on desktop) to document.body so position:fixed
   * covers the viewport when the header is sticky.
   * @param {HTMLDetailsElement} details
   */
  #mountToBody(details) {
    this.#mountBackdropToBody(details);

    if (isDesktopDrawerViewport()) {
      this.#mountDrawerToBody(details);
    }
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #mountBackdropToBody(details) {
    const backdrop = details.querySelector('.menu-drawer__backdrop');
    if (!(backdrop instanceof HTMLElement) || backdrop.dataset.teleported === 'true') return;

    const placeholder = document.createComment('menu-drawer-backdrop');
    backdrop.before(placeholder);
    this.#backdropPlaceholder = placeholder;

    backdrop.dataset.teleported = 'true';

    if (isDesktopDrawerViewport()) {
      backdrop.dataset.teleportedDesktop = 'true';
    }

    document.body.appendChild(backdrop);
    backdrop.addEventListener('click', this.#onTeleportedClick, true);
    this.#backdropAnimationFrame = requestAnimationFrame(() => {
      this.#backdropAnimationFrame = null;

      if (backdrop.dataset.teleported === 'true') {
        backdrop.classList.add('menu-drawer__backdrop--visible');
      }
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #mountDrawerToBody(details) {
    const drawer = details.querySelector('.menu-drawer');
    if (!(drawer instanceof HTMLElement) || drawer.dataset.teleported === 'true') return;

    const placeholder = document.createComment('menu-drawer-panel');
    drawer.before(placeholder);
    this.#drawerPlaceholder = placeholder;

    drawer.dataset.teleported = 'true';
    document.body.appendChild(drawer);
    drawer.addEventListener('click', this.#onTeleportedClick, true);
    drawer.addEventListener('keyup', this.#onKeyUp);
    this.#drawerAnimationFrame = requestAnimationFrame(() => {
      this.#drawerAnimationFrame = null;

      if (drawer.dataset.teleported === 'true') {
        drawer.classList.add('menu-drawer--visible');
      }
    });
  }

  /**
   * Restore teleported backdrop and drawer panel inside the details element.
   * @param {HTMLDetailsElement} details
   */
  #restoreFromBody(details) {
    this.#restoreDrawer(details);
    this.#restoreBackdrop(details);
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #restoreBackdrop(details) {
    this.#cancelBackdropAnimation();

    const backdrop = document.body.querySelector('.menu-drawer__backdrop[data-teleported="true"]');
    const placeholder = this.#backdropPlaceholder;

    if (!(backdrop instanceof HTMLElement) || !placeholder?.parentNode) return;

    backdrop.removeEventListener('click', this.#onTeleportedClick, true);
    backdrop.classList.remove('menu-drawer__backdrop--visible');
    placeholder.parentNode.insertBefore(backdrop, placeholder);
    placeholder.remove();
    backdrop.removeAttribute('data-teleported');
    backdrop.removeAttribute('data-teleported-desktop');
    this.#backdropPlaceholder = null;
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #restoreDrawer(details) {
    this.#cancelDrawerAnimation();

    const drawer = document.body.querySelector('.menu-drawer[data-teleported="true"]');
    const placeholder = this.#drawerPlaceholder;

    if (!(drawer instanceof HTMLElement) || !placeholder?.parentNode) return;

    if (drawer instanceof HTMLElement) {
      drawer.removeEventListener('click', this.#onTeleportedClick, true);
      drawer.removeEventListener('keyup', this.#onKeyUp);
    }

    drawer.classList.remove('menu-drawer--visible');
    placeholder.parentNode.insertBefore(drawer, placeholder);
    placeholder.remove();
    drawer.removeAttribute('data-teleported');
    this.#drawerPlaceholder = null;
  }

  #cancelMountAnimations() {
    this.#cancelBackdropAnimation();
    this.#cancelDrawerAnimation();
  }

  #cancelBackdropAnimation() {
    if (this.#backdropAnimationFrame === null) return;

    cancelAnimationFrame(this.#backdropAnimationFrame);
    this.#backdropAnimationFrame = null;
  }

  #cancelDrawerAnimation() {
    if (this.#drawerAnimationFrame === null) return;

    cancelAnimationFrame(this.#drawerAnimationFrame);
    this.#drawerAnimationFrame = null;
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
